import { Injectable } from '@angular/core';

import {
  ChartShowModes,
  ChartSortModes,
  FieldItem,
  FieldKinds,
  Form,
  Project,
  TableCellData,
  TableCellKinds,
  Team,
} from '@site-mate/dashpivot-shared-library';

import { IFormWithOrgTemplate } from 'app/form/form-with-org-template.model';
import { ChartBreakdownStaticOptions } from 'app/shared/model/charts/chart-breakdown-static-options.model';
import { ChartSettings } from 'app/shared/model/charts/chart-settings.model';
import { ChartData } from 'app/shared/model/charts/ratio/chart-data.model';

@Injectable({ providedIn: 'root' })
export class RatioChartDataParserService {
  public readonly sortableShowModes = [ChartShowModes.VerticalBar, ChartShowModes.HorizontalBar];
  private readonly mappers = {
    [ChartBreakdownStaticOptions.createdBy.value]: (carry: ChartData[], form: Form) => {
      return this.createOrUpdateByName(carry, form.createdBy?.fullName);
    },
    [ChartBreakdownStaticOptions.project.value]: (carry: ChartData[], form: Form) => {
      const template = form.template as unknown as { project: Project };
      return this.createOrUpdateByName(carry, template.project.name);
    },
    [ChartBreakdownStaticOptions.team.value]: (carry: ChartData[], form: Form) => {
      const template = form.template as unknown as { team: Team };
      return this.createOrUpdateByName(carry, template.team.name);
    },
    [ChartBreakdownStaticOptions.workflowColumns.value]: (carry: ChartData[], form: IFormWithOrgTemplate) => {
      const { columns } = form.orgTemplate as unknown as { columns: { title: string }[] };
      const currentColumn = columns[form.isInColumn - 1];
      return this.createOrUpdateByName(carry, currentColumn.title);
    },
  };

  public parse(forms: Form[], chart: ChartSettings) {
    return {
      data: this.processData(forms, chart),
    };
  }

  private processData(forms: Form[], chart: ChartSettings): ChartData[] {
    const formsWithOrgTemplate = forms.map((form) => ({ ...form, orgTemplate: chart.template }));
    const processedData = formsWithOrgTemplate
      .filter((form) => this.filterByTemplate(form, chart.templateId))
      .reduce(
        this.mappers[chart.breakdownBy] || this.getCategoryProcessor(chart.breakdownBy),
        [] as ChartData[],
      )
      .map((data, _index, entries) => {
        const total = entries.reduce((carry, item) => carry + item.value, 0);
        return {
          ...data,
          extra: {
            ...data.extra,
            total,
          },
        };
      });
    return this.sortByCriteria(processedData, chart);
  }

  private getCategoryProcessor(selectedField: string) {
    const [fieldId, columnId] = selectedField.split('.');
    return (carry: ChartData[], form: Form) => {
      const categories = this.getFormCategories(form, fieldId, columnId);
      const multipleEntries = 'Multiple';
      const noEntries = 'No selection';
      const singular = 1;
      if (categories.length > singular) {
        return this.createOrUpdateByName(carry, multipleEntries);
      }
      if (categories.length === singular) {
        const [category] = categories;
        return this.createOrUpdateByName(carry, category);
      }
      return this.createOrUpdateByName(carry, noEntries);
    };
  }

  private getFormCategories(form: Form, fieldId: string, columnId: string) {
    const tableTypes = [FieldKinds.Table, FieldKinds.PrefilledTable];
    const topLevelCategories = form.items
      .filter((item) => item._id === fieldId && item.kind === FieldKinds.Category)
      .reduce((carry, item) => carry.concat(...item.selectedItems), [] as FieldItem[])
      .map((item) => item.label || item.value || '');
    const tableCategories = form.items
      .filter((item) => item._id === fieldId && tableTypes.includes(item.kind))
      .reduce((carry, table) => {
        const columns = table.rows.reduce(
          (subCarry, row) => subCarry.concat(...row.columns),
          [] as TableCellData[],
        );
        return carry.concat(...columns);
      }, [] as TableCellData[])
      .filter((tableColumn) => tableColumn._id === columnId && tableColumn.kind === TableCellKinds.List)
      .map((list) => list.value || '');
    const categories = new Set<string>([...topLevelCategories, ...tableCategories]);
    return Array.from(categories);
  }

  private createOrUpdateByName(carry: ChartData[], name: string) {
    const groupIndex = carry.findIndex((entry) => entry.name === name);
    if (groupIndex !== -1) {
      const group = carry[groupIndex];
      group.value += 1;
    } else {
      carry.push({
        name,
        value: 1,
        extra: {},
      });
    }
    return carry;
  }

  private filterByTemplate(form: Form, templateId: string) {
    const template = form.template as unknown as { _id: string };
    return template._id === templateId || form.organisationTemplateId === templateId;
  }

  private sortByCriteria(data: ChartData[], chart: ChartSettings) {
    const sortModes = {
      [ChartSortModes.Alphabetical]: (previous: ChartData, next: ChartData) =>
        previous.name.localeCompare(next.name),
      [ChartSortModes.Volume]: (previous: ChartData, next: ChartData) => next.value - previous.value,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      none: (_previous: ChartData, _next: ChartData) => 0,
    };
    const sortMode = this.sortableShowModes.includes(chart.showAs)
      ? sortModes[chart.sortMode]
      : sortModes.none;
    return data.sort(sortMode);
  }
}
